<template>
  <transition name="settle">
    <!-- A region rather than a dialog: it asks for consent without trapping
         the reader, so nothing here may steal focus from the page. -->
    <div
      v-if="visible"
      class="cookie"
      role="region"
      aria-label="Hinweis zu Analytics"
    >
      <div class="cookie__plate">
        <p class="cookie__text">
          wir nutzen analytics zur anonymen nutzungsanalyse – nur mit deiner einwilligung.
          <router-link to="/datenschutz" class="cookie__link">mehr erfahren</router-link>
        </p>

        <div class="cookie__actions">
          <button type="button" class="cookie__button" @click="decline">ablehnen</button>
          <button
            type="button"
            class="cookie__button cookie__button--accept"
            @click="accept"
          >akzeptieren</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { inject } from '@vercel/analytics';

const STORAGE_KEY = 'cookie-consent';

/* Long enough for the floor to have drawn itself: the banner is the last thing
   to arrive, not something the reader meets before the shop. */
const APPEARANCE_DELAY = 1000;

export default {
  name: 'CookieBanner',
  data() {
    return { visible: false, analyticsLoaded: false, timer: null };
  },
  mounted() {
    const consent = this.readConsent();
    // Already answered once: honour it and stay out of the way. Analytics runs
    // only where it was actually accepted — a decline loads nothing at all.
    if (consent === 'accepted') this.loadAnalytics();
    else if (!consent) this.timer = window.setTimeout(() => { this.visible = true; }, APPEARANCE_DELAY);
  },
  beforeUnmount() {
    window.clearTimeout(this.timer);
  },
  methods: {
    /* Storage can be blocked outright (private mode, a locked-down browser).
       Reading null there means the banner shows again next time, which is the
       safe way round: it never treats a failure as consent. */
    readConsent() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch (error) {
        return null;
      }
    },
    writeConsent(value) {
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch (error) {
        // The answer still holds for this visit; it just cannot be remembered.
      }
    },
    loadAnalytics() {
      if (this.analyticsLoaded) return;
      inject();
      this.analyticsLoaded = true;
    },
    accept() {
      this.writeConsent('accepted');
      this.loadAnalytics();
      this.visible = false;
    },
    decline() {
      this.writeConsent('declined');
      this.visible = false;
    },
  },
};
</script>

<style scoped>
/* Above the drawer, which is 300: a reader who opened the cart before
   answering still has to be able to answer. */
.cookie {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 400;
  display: flex;
  justify-content: center;
  padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom));
  pointer-events: none;
}

/* The plate every other floating thing on this site is drawn as: opaque,
   because a notice you can read the carpet through is a notice nobody reads. */
.cookie__plate {
  position: relative;
  width: min(520px, 100%);
  padding: 1.4rem 1.6rem 1.5rem;
  pointer-events: auto;

  background: #faf6f0;
  border: 1px solid rgba(58, 43, 30, 0.34);
  border-radius: 2px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.6),
    0 18px 36px -18px rgba(50, 35, 20, 0.7);
}

.cookie__plate::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid rgba(58, 43, 30, 0.22);
  border-radius: 1px;
  pointer-events: none;
}

.cookie__text {
  color: #3a2b1e;
  font-size: 0.88rem;
  line-height: 1.6;
  text-align: center;
}

.cookie__link {
  color: #2e4057;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.cookie__link:focus-visible {
  outline: 2px solid #2e4057;
  outline-offset: 3px;
}

.cookie__actions {
  display: flex;
  gap: 0.7rem;
  margin-top: 1.2rem;
}

.cookie__button {
  flex: 1;
  padding: 0.75rem 1.2rem;

  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;

  color: #3a2b1e;
  background: rgba(250, 246, 240, 0.94);
  border: 1px solid rgba(58, 43, 30, 0.34);
  border-radius: 2px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.6),
    0 10px 22px -14px rgba(50, 35, 20, 0.65);
  transition: color 0.25s ease, background-color 0.25s ease, border-color 0.25s ease;
}

.cookie__button:hover,
.cookie__button:focus-visible {
  color: #2e4057;
  border-color: #2e4057;
}

/* Filled in the accent, like the checkout and the buy button: of the two
   answers this is the one the page is asking for. */
.cookie__button--accept {
  color: #fdfaf6;
  background: #2e4057;
  border-color: #2e4057;
  box-shadow: 0 12px 24px -14px rgba(20, 30, 45, 0.9);
}

.cookie__button--accept:hover,
.cookie__button--accept:focus-visible {
  color: #fdfaf6;
  background: #24344a;
  border-color: #24344a;
}

.cookie__button:focus-visible {
  outline: 2px solid #2e4057;
  outline-offset: 3px;
}

/* Laid onto the carpet rather than slid in from off-screen: the same short
   drop the plates on the other pages settle with, so the notice arrives as one
   more thing on the floor. */
.settle-enter-active {
  transition: transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.45s ease;
}

.settle-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.settle-enter-from,
.settle-leave-to {
  opacity: 0;
  transform: translateY(18px);
}

@media (max-width: 480px) {
  .cookie__plate {
    padding: 1.2rem 1.2rem 1.3rem;
  }

  .cookie__text {
    font-size: 0.84rem;
  }
}

/* The answer still has to arrive; it just arrives without moving. */
@media (prefers-reduced-motion: reduce) {
  .settle-enter-active,
  .settle-leave-active {
    transition: opacity 0.2s ease;
  }

  .settle-enter-from,
  .settle-leave-to {
    transform: none;
  }

  .cookie__button {
    transition: none;
  }
}
</style>
